// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Module Layout with sub-navigation
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  IconLayoutDashboard, IconSchool, IconBook,
  IconCertificate, IconClipboardList,
  IconBuildingCommunity, IconBell, IconMail,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useBlackboardStore } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import type { NavItem } from '../../types/nav';
import { brand } from '../../lib/colors';
import PageHeader from '../../components/ui/PageHeader';
import ModuleNav from '../../components/layout/ModuleNav';

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/blackboard', icon: IconLayoutDashboard, exact: true },
  { label: 'My Training', path: '/blackboard/my-training', icon: IconSchool },
  { label: 'Classrooms', path: '/blackboard/classrooms', icon: IconBuildingCommunity },
  { label: 'Training Library', path: '/blackboard/library', icon: IconBook },
  { label: 'Records', path: '/blackboard/records', icon: IconClipboardList, minLevel: 'editor' },
  { label: 'Position Plans', path: '/blackboard/positions', icon: IconCertificate, minLevel: 'editor' },
  { label: 'Certificates', path: '/blackboard/certificates', icon: IconCertificate, minLevel: 'editor' },
  { label: 'Communications', path: '/blackboard/comms', icon: IconMail, minLevel: 'editor' },
  { label: 'Notifications', path: '/blackboard/notifications', icon: IconBell },
];

export default function BlackboardLayout() {
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('blackboard', NAV_ITEMS);
  const { messages, loadMessages } = useBlackboardStore();
  const { user } = useAuthStore();

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const unreadCount = useMemo(() => {
    const email = user?.email?.toLowerCase() || '';
    if (!email) return 0;
    return messages.filter(m =>
      m.recipientEmail?.toLowerCase() === email &&
      (m.channel === 'inbox' || m.channel === 'both') &&
      !m.readAt
    ).length;
  }, [messages, user?.email]);

  // Inject unread badge onto My Training nav item
  const itemsWithBadge = useMemo(() =>
    visibleItems.map(item =>
      item.path === '/blackboard/my-training' && unreadCount > 0
        ? { ...item, badge: unreadCount }
        : item
    ),
    [visibleItems, unreadCount]
  );

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader
        breadcrumbs={[{ label: 'Prepare' }, { label: 'Blackboard' }]}
        title="Blackboard"
        subtitle="Training catalog, certification records and compliance tracking"
      />
      <ModuleNav items={itemsWithBadge} accentColor={brand.blurple} />
      <Outlet />
    </div>
  );
}
